import cloneDeep from 'lodash-es/cloneDeep';
import { CrudApiClient } from "../../api-clients";
import { BaseNMCollection } from "../../domain-models";
import { NmOCCModel } from '../../domain-models/nm/nmocc-model';
import { ZoomAdvancedOptions } from "../../domain-models/find-options";
import { MetaDataUtils } from '../../meta-data';
import { AggregateMetaData } from "../../meta-data/aggregate-meta-data";
import { MasterDetailOrchestratorViewModel } from "../master-detail-orchestrator-view-model";
import { ZoomUIStarterArgs } from "../zoom/zoom-ui-starter-args";
import { NmMasterDetailRootViewModel } from './nm-master-detail-root-view-model';
import { ExternalViewModel } from '../external-view-model';
import { ItemViewModel } from '../item-view-model';
import { CollectionViewModel } from '../collection-view-model';
import type { IdentityInterface, ModelInterface, ServiceResponseInterface } from '@nts/std/interfaces';

export abstract class NmMasterDetailOrchestratorViewModel<

    /**
     * ViewModel principale
     */
    TViewModel extends NmMasterDetailRootViewModel<TModel, TIdentity, TRootDiscriminator, TItem, TItemIdentity, TOtherExternal, TOtherExternalIdentity, TNMCollection, TRootDiscriminatorViewModel, TItemViewModel, TNMCollectionViewModel>,

    /**
     * ApiClient
     */
    TApiClient extends CrudApiClient<TModel, TIdentity>,

    /**
     * Model principale
     */
    TModel extends NmOCCModel<TRootDiscriminator, TIdentity, TItem, TItemIdentity, TOtherExternal, TOtherExternalIdentity, TNMCollection>,

    /**
     * Identity del Model principale
     */
    TIdentity extends IdentityInterface,

    /**
     * Model che rappresenta l'estremità principale della relazione N:M (sarà utilizzato come discriminante per caricare la collection delle associazioni)
     */
    TRootDiscriminator extends ModelInterface<TIdentity>,

    /**
     * Tipo dell'associazione N:M
     */
    TItem extends ModelInterface<TItemIdentity>,

    /**
     * Idenity dell'associazione N:M
     */
    TItemIdentity extends IdentityInterface,

    /**
     * Estremità opposta al discriminatore della relazione N:M
     */
    TOtherExternal extends ModelInterface<TOtherExternalIdentity>,

    /**
     * Idenity dell'estremità opposta al discriminatore della relazione N:M
     */
    TOtherExternalIdentity extends IdentityInterface,

    /**
     * Tipo della collection di associazioni
     */
    TNMCollection extends BaseNMCollection<TItem, TItemIdentity, TRootDiscriminator, TIdentity, TOtherExternal, TOtherExternalIdentity>,

    /**
     * ViewModel che rappresenta l'estremità principale della relazione N:M (sarà utilizzato come discriminante per caricare la collection delle associazioni)
     */
    TRootDiscriminatorViewModel extends ExternalViewModel<TRootDiscriminator, TIdentity>,

    /**
     * ViewModel del tipo dell'associazione N:M
     */
    TItemViewModel extends ItemViewModel<TItem, TItemIdentity>,

    /**
     * CollectionViewModel del tipo della collection di associazioni
     */
    TNMCollectionViewModel extends CollectionViewModel<TItemViewModel, TItem, TItemIdentity>,
> extends MasterDetailOrchestratorViewModel<TViewModel, TApiClient, TModel, TIdentity> {

    public override async getByJsonIdentity(jsonIdentity: string): Promise<ServiceResponseInterface> {
        const associationIdentityObject = JSON.parse(jsonIdentity);


        const identity = new this.identityType();

        // Se arriva l'identity dell'nm allora la uso
        if (associationIdentityObject?.identityType === identity.identityType) {
            for (const key of Object.keys(associationIdentityObject)) {
                if (key !== 'identityType') {
                    identity[key] = associationIdentityObject[key];
                }
            }
        } else {
            // Se arriva l'identity della collecion items allora devo mapparla con l'identity dell'nm

            // Recupero il metadato della collection items
            const collectionMetaData = this.metadata.rootMetaData.internalCollections.find((internal) =>
                internal.principalPropertyName === 'Items'
            );

            if (collectionMetaData) {
                // Recupero la lista delle property da impostare nell'identity dell'nm in base all'assocaition e le identity degli elementi della collection
                const associationProperties = collectionMetaData.associationProperties.filter((a) => collectionMetaData.dependentMetaData.identityNames.indexOf(a.dependentPropertyName) > -1);

                for (const association of associationProperties) {
                    const camelIdentityName = MetaDataUtils.toCamelCase(association.principalPropertyName);
                    identity[camelIdentityName] = associationIdentityObject[MetaDataUtils.toCamelCase(association.dependentPropertyName)];
                }
            }
        }

        return await this.getByIdentity(identity);

    }

    protected override getZoomUIStarterArgsFromFind(
        aggregateMetaData: AggregateMetaData,
        requestedDomainModelName: string,
        requestedDomainModelFullName: string,
        isRemote: boolean,
        callerAggregateMetaData: AggregateMetaData,
        rootDomainModelName: string,
        zoomOptions: ZoomAdvancedOptions
    ): ZoomUIStarterArgs {

        // Recupero objectAssociation AggregateMetaData
        const objectAssociationAggregateMetaData = cloneDeep(aggregateMetaData) as AggregateMetaData;

        const objectAssociationMetaData = objectAssociationAggregateMetaData.rootMetaData.internalCollections.find((internal) =>
            internal.principalPropertyName === 'Items'
        ).dependentMetaData;

        objectAssociationAggregateMetaData.domainModels = [objectAssociationMetaData];

        objectAssociationAggregateMetaData.rootMetaData = objectAssociationMetaData;
        objectAssociationAggregateMetaData.rootName = objectAssociationMetaData.name;
        objectAssociationAggregateMetaData.rootFullName = objectAssociationMetaData.fullName;

        return new ZoomUIStarterArgs(
            objectAssociationAggregateMetaData,
            objectAssociationAggregateMetaData.rootMetaData.name,
            objectAssociationAggregateMetaData.rootMetaData.fullName,
            false,
            aggregateMetaData,
            rootDomainModelName,
            zoomOptions
        );
    }
}
